import { useRef, useState } from "react"

// All draft writes share one queue. A failed write must not prevent the next
// attempt, and an older result must not describe edits made while it was in flight.
export function useDraftSave({
  saveDraft,
  getSnapshot,
  getPayload,
  preparePayload = (payload) => payload,
  onSaving = () => {},
  onSaved = () => {},
  onFailed = () => {},
  canRetry = () => true,
}) {
  const [savedAt, setSavedAt] = useState(null)
  const [saveError, setSaveError] = useState(null)
  const [retryingSave, setRetryingSave] = useState(false)
  const chainRef = useRef(Promise.resolve())
  const editRevisionRef = useRef(0)
  const saveAttemptRef = useRef(0)

  const noteDraftEdit = () => {
    editRevisionRef.current += 1
    setSavedAt(null)
    setSaveError(null)
  }

  const queueDraftSave = (snapshot, capturedPayload) => {
    const request = chainRef.current
      .catch(() => {})
      .then(async () => {
        const payload = preparePayload(capturedPayload)
        onSaving(snapshot, payload)
        try {
          const draft = await saveDraft(snapshot.draftId, payload)
          onSaved(snapshot, payload)
          return draft
        } catch (error) {
          onFailed(snapshot, payload, error)
          throw error
        }
      })
    chainRef.current = request
    return request
  }

  // `snapshot` saves a proposed state the page has not committed yet.
  const saveCurrentDraft = async ({ requireLatest = false, snapshot: proposed = null } = {}) => {
    const snapshot = proposed || getSnapshot()
    const revision = editRevisionRef.current
    const attempt = ++saveAttemptRef.current
    setSavedAt(null)

    try {
      const draft = await queueDraftSave(snapshot, getPayload(snapshot))
      if (revision === editRevisionRef.current && attempt === saveAttemptRef.current) {
        setSaveError(null)
        setSavedAt(Date.now())
      } else if (requireLatest && revision !== editRevisionRef.current) {
        const error = new Error("Your draft changed while saving. Please try again.")
        setSaveError(error.message)
        throw error
      }
      return draft
    } catch (error) {
      if (revision === editRevisionRef.current && attempt === saveAttemptRef.current) {
        setSavedAt(null)
        setSaveError(error.message || "Draft could not be saved. Please try again.")
      }
      throw error
    }
  }

  const retrySave = async () => {
    if (!canRetry() || !getSnapshot().draftId || retryingSave) return
    setRetryingSave(true)
    try {
      await saveCurrentDraft({ requireLatest: true })
    } catch {
      // Keep the error visible for another attempt.
    } finally {
      setRetryingSave(false)
    }
  }

  return { savedAt, saveError, retryingSave, noteDraftEdit, saveCurrentDraft, retrySave }
}
